import type { AdItem, ItemCategory } from '@sbt-web/network/types';
import { getCategory } from '@sbt-web/tracking';
import { urnTransformer } from '@sbt-web/utils';
import { buildSearchWithRank } from '@/tools/tracking/transformer/SearchTransformer';

type ListingSection =
  | 'listing'
  | 'gallery'
  | 'geoboost'
  | 'homepage_recommender'
  | 'detail_recommender';

export type TrackingItem = {
  adItemUrn: string;
  adItemSubject: string;
  adItemCategory: ItemCategory;
  searchId: string;
  adItemRank: number;
  adId: number;
  listingSection: ListingSection;
};

type TrackingData = {
  type: string;
  object: {
    type: string;
    id: string;
    name: string;
    category: string;
    adId: number;
    'spt:custom'?: {
      listing_section: ListingSection;
    };
  };
  search: {
    '@id': string;
    '@type': 'SearchMetadata';
    source: 'MainSearch';
    rank: number;
  };
};

export const getTrackingItem = (
  item: AdItem,
  listingSection: ListingSection,
  searchId: string = '',
  adItemRank: number = -1
): TrackingItem => {
  const adId = urnTransformer(item.urn)?.adId ?? '0';
  return {
    adItemUrn: item.urn,
    adItemSubject: item.subject,
    adItemCategory: item.category,
    searchId,
    adItemRank,
    listingSection,
    // The adId computed below can be NaN with the ads created with the new form.
    // We tried to use the string value, instead of a number, but the model
    // doesn't support a string value.
    adId: Number.parseInt(adId),
  };
};

export const buildCardImpressionTrackingViewData = (
  adItem: TrackingItem
): TrackingData => ({
  type: 'Watch',
  ...buildCommonData(adItem),
});

export const buildCardClickTrackingData = (
  adItem: TrackingItem
): TrackingData => ({
  type: 'Click',
  ...buildCommonData(adItem),
});

const buildCommonData = (adItem: TrackingItem): Omit<TrackingData, 'type'> => {
  const {
    adItemUrn,
    adItemSubject,
    adItemCategory,
    searchId,
    adItemRank,
    adId,
    listingSection,
  } = adItem;

  return {
    object: {
      type: 'ClassifiedAd',
      id: adItemUrn,
      name: adItemSubject,
      category: getCategory(adItemCategory.id) || '',
      adId,
      'spt:custom': {
        listing_section: listingSection,
      },
    },
    search: buildSearchWithRank(searchId, adItemRank),
  };
};
